import type {
  ComponentReservationStatus,
  ProductCollaborationStatus,
} from '../types'

interface Props {
  products: ProductCollaborationStatus[]
  isLoading: boolean
  error: Error | null
}

const stateLabel: Record<string, string> = {
  draft: 'Draft',
  component_editing: 'Editing',
  merge_review: 'Merge review',
  approved: 'Approved',
  rejected: 'Rejected',
}

function reservationLabel(reservation: ComponentReservationStatus | null) {
  if (!reservation) return 'Free'
  if (reservation.state === 'held') return `Held by ${reservation.holder_id}`
  return reservation.state.charAt(0).toUpperCase() + reservation.state.slice(1)
}

export function ProductCollaborationPanel({ products, isLoading, error }: Props) {
  if (isLoading) {
    return <p className="product-status-empty">Loading product status…</p>
  }
  if (error) {
    return <p className="product-status-empty">Product status unavailable.</p>
  }
  if (products.length === 0) {
    return <p className="product-status-empty">No object products.</p>
  }

  return (
    <div className="product-status-list">
      {products.map((product) => (
        <section className="product-status" key={product.product_id}>
          <header className="product-status__header">
            <div>
              <div className="product-status__title">
                <i className="fas fa-cube" aria-hidden="true" />
                {product.owner}
              </div>
              <div className="product-status__meta">
                {product.object_type.toUpperCase()} · revision {product.current_revision}
              </div>
            </div>
            <span className={`product-state product-state--${product.state}`}>
              {stateLabel[product.state] || product.state}
            </span>
          </header>

          <div className="product-status__revisions">
            <span>Rendered <b>{product.rendered_revision ?? 'pending'}</b></span>
            {product.candidate_revision !== null && (
              <span>Candidate <b>{product.candidate_revision}</b></span>
            )}
            <span>Profile <b>{product.validation_profile}</b></span>
          </div>

          <div className="product-components">
            {product.components.map((component) => {
              const reservation = component.reservation
              return (
                <div className="product-component" key={component.component_id}>
                  <span
                    className={`reservation-dot reservation-dot--${reservation?.state || 'free'}`}
                    title={reservationLabel(reservation)}
                  />
                  <div className="product-component__main">
                    <div className="product-component__name">
                      {component.component_id}
                      <span>r{component.current_revision}</span>
                    </div>
                    <div className="product-component__status">
                      {reservationLabel(reservation)}
                    </div>
                    {reservation?.depends_on.length ? (
                      <div className="product-component__dependency">
                        Depends on {reservation.depends_on.join(', ')}
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
